import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { TargetState } from '@pyxl/shared';

/**
 * Difficulty tier
 */
export type DifficultyTier = 'beginner' | 'pro' | 'elite';

/**
 * Difficulty configuration per tier
 */
export interface DifficultyConfig {
  tier: DifficultyTier;
  label: string;
  description: string;
  perfectThreshold: number;  // Max px error for Perfect
  goodThreshold: number;     // Max px error for Good
  maxAttempts: number;
  maxReveals: number;
  showTargetGhost: boolean;
  showPixelError: boolean;
  showErrorIndicator: boolean;
  timeMultiplier: number;    // Score bonus for speed
}

/**
 * Difficulty presets
 */
export const DIFFICULTY_CONFIGS: Record<DifficultyTier, DifficultyConfig> = {
  beginner: {
    tier: 'beginner',
    label: 'Beginner',
    description: 'Target guides visible, tighter tolerances',
    perfectThreshold: 4,   // Tightened from 8px
    goodThreshold: 10,     // Tightened from 15px
    maxAttempts: 1,
    maxReveals: 0,
    showTargetGhost: true,
    showPixelError: false,
    showErrorIndicator: false,
    timeMultiplier: 1,
  },
  pro: {
    tier: 'pro',
    label: 'Pro',
    description: 'No guides, text instructions only',
    perfectThreshold: 2,   // Tightened from 3px
    goodThreshold: 5,      // Tightened from 6px
    maxAttempts: 1,
    maxReveals: 0,
    showTargetGhost: false,
    showPixelError: false,
    showErrorIndicator: false,
    timeMultiplier: 2,
  },
  elite: {
    tier: 'elite',
    label: 'Elite',
    description: 'One shot, pixel-perfect precision',
    perfectThreshold: 1,   // Already tight
    goodThreshold: 2,
    maxAttempts: 1,
    maxReveals: 0,
    showTargetGhost: false,
    showPixelError: false,
    showErrorIndicator: false,
    timeMultiplier: 3,
  },
};

/**
 * Game object state
 */
export interface GameObject {
  id: string;
  type: 'rect' | 'circle' | 'text';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  stroke: number;
  radius: number;
  color?: string;     // Optional custom color
  draggable?: boolean; // Whether the object can be moved by the player
}

/**
 * Target state for scoring — canonical type from @pyxl/shared
 */
export type { TargetState } from '@pyxl/shared';

/**
 * Reference object - non-draggable guides visible to user
 */
export interface ReferenceObject {
  id: string;
  type: 'rect' | 'circle' | 'line' | 'text';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  color: string;
  label?: string | undefined;
}

/**
 * Level state
 */
export interface LevelState {
  id: string;
  mode: 'quickPick' | 'precisionDrag' | 'slider' | 'composite';
  skillLane: 'align' | 'spacing' | 'scale' | 'rotate' | 'typefit' | 'style' | 'bezier';
  instruction?: string | undefined;  // Text instruction for Pro/Elite modes
  objects: GameObject[];
  referenceObjects: ReferenceObject[];  // Non-draggable guide objects
  targets: TargetState[];
  constraints: {
    dof: string[];
    maxAttempts: number;
    maxReveals: number;
    referenceVisibility: 'always' | 'flash' | 'hidden';
    flashDurationMs: number;
  };
}

/**
 * Attempt result
 */
export interface AttemptResult {
  attemptNumber: number;
  timestamp: number;
  error: {
    dx: number;
    dy: number;
    dw: number;
    dh: number;
    dTheta: number;
    dStroke: number;
    dRadius: number;
  };
  grade: 'Perfect' | 'Good' | 'Off';
  score: number;
}

/**
 * Game store state
 */
interface GameState {
  // Difficulty
  difficulty: DifficultyTier;
  difficultyConfig: DifficultyConfig;
  
  // Current level
  currentLevel: LevelState | null;
  levelIndex: number;
  
  // Objects
  objects: GameObject[];
  selectedObjectId: string | null;
  
  // Gameplay state
  attempts: number;
  revealsUsed: number;
  startTime: number | null;
  submitted: boolean;
  
  // Results
  attemptResults: AttemptResult[];
  currentGrade: 'Perfect' | 'Good' | 'Off' | null;

  // Flash reference
  flashReferenceVisible: boolean;

  // Actions
  setDifficulty: (tier: DifficultyTier) => void;
  loadLevel: (level: LevelState) => void;
  resetLevel: () => void;
  selectObject: (id: string | null) => void;
  updateObject: (id: string, updates: Partial<GameObject>) => void;
  submit: () => void;
  useReveal: () => void;
  setGrade: (grade: 'Perfect' | 'Good' | 'Off') => void;
  addAttemptResult: (result: AttemptResult) => void;
  setFlashReferenceVisible: (visible: boolean) => void;
  nextLevel: () => void;
}

export const useGameStore = create<GameState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    difficulty: 'beginner' as DifficultyTier,
    difficultyConfig: DIFFICULTY_CONFIGS.beginner,
    currentLevel: null,
    levelIndex: 0,
    objects: [],
    selectedObjectId: null,
    attempts: 0,
    revealsUsed: 0,
    startTime: null,
    submitted: false,
    attemptResults: [],
    currentGrade: null,
    flashReferenceVisible: false,

    // Actions
    setDifficulty: (tier) =>
      set({
        difficulty: tier,
        difficultyConfig: DIFFICULTY_CONFIGS[tier],
      }),

    loadLevel: (level) => {
      // Randomize draggable object positions on load
      const randomizedObjects = level.objects.map((obj) => {
        if (obj.draggable) {
          const offsetX = (Math.random() - 0.5) * 200;
          const offsetY = (Math.random() - 0.5) * 200;
          return {
            ...obj,
            x: Math.max(50, Math.min(700, obj.x + offsetX)),
            y: Math.max(50, Math.min(500, obj.y + offsetY)),
          };
        }
        return { ...obj };
      });
      
      set({
        currentLevel: level,
        objects: randomizedObjects,
        selectedObjectId: null,
        attempts: 0,
        revealsUsed: 0,
        startTime: Date.now(),
        submitted: false,
        attemptResults: [],
        currentGrade: null,
        flashReferenceVisible: level.constraints.referenceVisibility === 'flash',
      });
    },

    resetLevel: () => {
      const { currentLevel } = get();
      if (currentLevel) {
        // Randomize draggable object positions
        const randomizedObjects = currentLevel.objects.map((obj) => {
          if (obj.draggable) {
            // Random offset within reasonable bounds
            const offsetX = (Math.random() - 0.5) * 200;
            const offsetY = (Math.random() - 0.5) * 200;
            return {
              ...obj,
              x: Math.max(50, Math.min(700, obj.x + offsetX)),
              y: Math.max(50, Math.min(500, obj.y + offsetY)),
            };
          }
          return { ...obj };
        });
        
        set({
          objects: randomizedObjects,
          selectedObjectId: null,
          attempts: 0,
          revealsUsed: 0,
          startTime: Date.now(),
          submitted: false,
          attemptResults: [],
          currentGrade: null,
          flashReferenceVisible: currentLevel.constraints.referenceVisibility === 'flash',
        });
      }
    },

    selectObject: (id) => set({ selectedObjectId: id }),

    updateObject: (id, updates) =>
      set((state) => ({
        objects: state.objects.map((obj) =>
          obj.id === id ? { ...obj, ...updates } : obj
        ),
      })),

    submit: () =>
      set((state) => ({
        attempts: state.attempts + 1,
        submitted: true,
      })),

    useReveal: () =>
      set((state) => ({
        revealsUsed: state.revealsUsed + 1,
      })),

    setGrade: (grade) => set({ currentGrade: grade }),

    // MH-FIX-002: Removed `submitted: false` — addAttemptResult should not
    // reset the submitted flag. The flag is set by submit() and should remain
    // true until resetLevel() or loadLevel() is called.
    addAttemptResult: (result) =>
      set((state) => ({
        attemptResults: [...state.attemptResults, result],
      })),

    setFlashReferenceVisible: (visible) => set({ flashReferenceVisible: visible }),

    // MH-FIX-002: Added `attemptResults: []` — nextLevel was not clearing
    // previous level's attempt results, causing them to bleed into the next level.
    nextLevel: () =>
      set((state) => ({
        levelIndex: state.levelIndex + 1,
        currentLevel: null,
        objects: [],
        selectedObjectId: null,
        attempts: 0,
        revealsUsed: 0,
        startTime: null,
        submitted: false,
        attemptResults: [],
        currentGrade: null,
        flashReferenceVisible: false,
      })),
  }))
);

/**
 * Selector: Get object by ID
 */
export const selectObjectById = (id: string) => (state: GameState) =>
  state.objects.find((obj) => obj.id === id);

/**
 * Selector: Get selected object
 */
export const selectSelectedObject = (state: GameState) =>
  state.selectedObjectId
    ? state.objects.find((obj) => obj.id === state.selectedObjectId)
    : null;

/**
 * Selector: Check if reveal is available
 */
export const selectIsRevealAvailable = (state: GameState) => {
  if (!state.currentLevel) return false;
  const { maxReveals } = state.currentLevel.constraints;
  return state.attempts >= 2 && state.revealsUsed < maxReveals;
};

/**
 * Selector: Get elapsed time in ms
 */
export const selectElapsedTime = (state: GameState) =>
  state.startTime ? Date.now() - state.startTime : 0;
